// @ts-nocheck
import { calculatePercentile } from "../../../util/utils";
import {
  fetchParameterRecords,
  fetchSolarEnergyForecast,
  getOHHFC,
  getTHEFW,
  getTHEFWwind80,
  interpolateCurveValues,
  interpolateCurveValuesNoDates,
} from "../../../api/awsLamba";
import { downloadData, getUrl } from "@aws-amplify/storage";
import { withTrailingSlash } from "../../../utils/s3Path";

const toIsoRecords = (arr: Array<{ x: string | number | Date; y: number }>) =>
  arr.map((item) => ({
    y: item.y,
    x: new Date(item.x).toISOString(),
  }));

const createObjectWithPlaceholders = (properties: string[], placeholder = "-") =>
  properties.reduce<Record<string, string>>((obj, prop) => {
    obj[prop] = placeholder;
    return obj;
  }, {});

export class AssetGraphService {
  static async fetchShortForecast(lat: number, lng: number) {
    const res = await getOHHFC(lat, lng);
    const { precip, solar, wind, temp, humidity, solarEnergyWeatherDataInputs } =
      res;
    return { precip, solar, wind, temp, humidity, solarEnergyWeatherDataInputs };
  }

  static async fetchForecasts(lat: number, lng: number) {
    const res = await getTHEFW(lat, lng);
    const {
      precip,
      solar,
      wind,
      temperature,
      humidity,
      soilMoisture,
      snowAcc,
      solarEnergyWeatherDataInputs,
    } = res;

    return {
      precip,
      solar,
      wind,
      temperature,
      humidity,
      soilMoisture,
      snowAcc,
      solarEnergyWeatherDataInputs,
      dateTimeCycle: res.dateTime,
    };
  }

  static async fetchWind80(lat: number, lng: number) {
    return getTHEFWwind80(lat, lng);
  }

  static async fetchSolarEnergyForecast(datta: unknown) {
    return fetchSolarEnergyForecast(datta);
  }

  static async fetchProcessedModelData(items: any[]) {
    // withTrailingSlash: a few rows were saved without it, and the replace below then matches nothing
    const processedModelFolder = withTrailingSlash(items[0].routeLong).replace(
      `/${items[0].routeShort}/`,
      ""
    );
    const processedModelRouteDir = `${processedModelFolder}/directory.json`;

    const { url } = await getUrl({ path: processedModelRouteDir });
    const resp = await fetch(url.toString(), { cache: "no-store" });
    if (!resp.ok) {
      throw new Error(`Failed to load directory.json: ${resp.status}`);
    }
    const recentProcessedModelDirectory = await resp.json();

    const promises = Object.keys(recentProcessedModelDirectory).map(
      async (key) => {
        const downloadProcessedModel = await downloadData({
          path: `${processedModelFolder}/${key}/${recentProcessedModelDirectory[key]}`,
        }).result;
        const jsonData = await downloadProcessedModel.body.text();
        const processedModelResult = JSON.parse(jsonData);
        return { key, processedModelResult };
      }
    );

    const results = await Promise.all(promises);

    return results.reduce((acc, { key, processedModelResult }) => {
      acc[key] = processedModelResult;
      return acc;
    }, {} as Record<string, any>);
  }

  static async fetchAdditionalDataSources(activeProcessedModel: any) {
    if (!activeProcessedModel?.additionalDataPoints?.length) {
      return null;
    }

    const dataSourcePromises = activeProcessedModel.additionalDataPoints.map(
      async (dataPoint: any) => {
        if (!dataPoint.parameterId) {
          return null;
        }

        try {
          const records = await fetchParameterRecords(
            dataPoint.parameterId,
            dataPoint.lookbackDays,
            dataPoint.source
          );
          return {
            ...dataPoint,
            records,
          };
        } catch {
          return null;
        }
      }
    );

    const results = await Promise.all(dataSourcePromises);
    const validResults = results.filter((result) => result !== null);
    return validResults.length ? validResults : null;
  }

  static buildWindPowerData(wind80: any, curveData: any[]) {
    const sortedCurveData = [...curveData].sort((a, b) => a.x - b.x);
    const percentiles = [10, 30, 50, 70, 90];
    const percentileWindPower = percentiles.reduce(
      (acc, curr) => ({ ...acc, [`p${curr}`]: [] }),
      {}
    );
    const percentileWindPowerInterpolated = JSON.parse(
      JSON.stringify(percentileWindPower)
    );

    const ensembleWind = JSON.parse(JSON.stringify(wind80.isoKeyMagEnsemble));
    const ensembleWindInterpolated = JSON.parse(
      JSON.stringify(wind80.isoKeyMagEnsembleInterpolate)
    );

    const windPowerAverageForecast: any[] = [];
    const windPowerAverageForecastInterpolated: any[] = [];

    Object.keys(ensembleWind).forEach((isoKey) => {
      const ensembles = ensembleWind[isoKey];
      ensembles.pop();
      const ensemblePowerArray = interpolateCurveValuesNoDates(
        sortedCurveData,
        ensembles
      );

      const windPowerAverage =
        ensemblePowerArray.reduce((acc, curr) => acc + curr) /
        ensemblePowerArray.length;
      windPowerAverageForecast.push({
        y: windPowerAverage,
        x: new Date(parseInt(isoKey)),
      });
      percentiles.forEach((pctl) => {
        const pctlEnsemblePower = calculatePercentile(
          ensemblePowerArray,
          pctl
        );
        percentileWindPower[`p${pctl}`].push({
          y: pctlEnsemblePower,
          x: new Date(parseInt(isoKey)),
        });
      });
    });

    Object.keys(ensembleWindInterpolated).forEach((isoKey) => {
      const ensembles = ensembleWindInterpolated[isoKey];
      ensembles.pop();
      const ensemblePowerArray = interpolateCurveValuesNoDates(
        sortedCurveData,
        ensembles
      );

      const windPowerAverage =
        ensemblePowerArray.reduce((acc, curr) => acc + curr) /
        ensemblePowerArray.length;
      windPowerAverageForecastInterpolated.push({
        y: windPowerAverage,
        x: new Date(parseInt(isoKey)),
      });
      percentiles.forEach((pctl) => {
        const pctlEnsemblePower = calculatePercentile(
          ensemblePowerArray,
          pctl
        );
        percentileWindPowerInterpolated[`p${pctl}`].push({
          y: pctlEnsemblePower,
          x: new Date(parseInt(isoKey)),
        });
      });
    });

    return {
      raw: {
        Power: {
          average: windPowerAverageForecast,
          control: interpolateCurveValues(sortedCurveData, wind80.raw.Speed.control),
          percentiles: percentileWindPower,
        },
      },
      interpolated: {
        Power: {
          average: windPowerAverageForecastInterpolated,
          control: interpolateCurveValues(
            sortedCurveData,
            wind80.interpolated.Speed.control
          ),
          percentiles: percentileWindPowerInterpolated,
        },
      },
    };
  }

  static buildHighResWindPowerData(highResWind80Speed: any, curveData: any[]) {
    const sortedCurveData = [...curveData].sort((a, b) => a.x - b.x);
    return interpolateCurveValues(sortedCurveData, highResWind80Speed);
  }

  static buildFlatProcessedModelData(
    processedModelData: any,
    forecast: string,
    fcVariation: string
  ) {
    const processedMDLData =
      processedModelData?.[forecast]?.models?.[fcVariation]?.data;
    if (!processedMDLData) {
      return null;
    }

    return Object.keys(processedMDLData).map((dateKey) => {
      const dataPoint = processedMDLData[dateKey];
      const recordData: Record<string, string> = {};
      for (const percentile in dataPoint) {
        let percentileLabel = percentile;
        if (!isNaN(percentile as any)) {
          percentileLabel = `p${parseInt(parseFloat(percentile) * 100)}`;
        }
        recordData[percentileLabel] = dataPoint[percentile].toFixed(3);
      }
      recordData["dateTime"] = new Date(dateKey).toISOString();
      return recordData;
    });
  }

  static buildFlatData(params: {
    forecast: string;
    fcVariation: string;
    interpolateChecked: boolean;
    processedModelData: any;
    precipitation: any;
    wind10: any;
    wind80: any;
    windPower80Data: any;
    solarPowerData: any;
    solarRadiation: any;
    temperature2m: any;
    humidity2m: any;
    soilMoisture: any;
    snowAcc: any;
    highResPrecip: any;
    highResWind10: any;
    highResWind80: any;
    highResWindPower80Data: any;
    highResSolar: any;
    highResTemp: any;
    highResHumidity: any;
  }) {
    const {
      forecast,
      fcVariation,
      interpolateChecked,
      processedModelData,
      precipitation,
      wind10,
      wind80,
      windPower80Data,
      solarPowerData,
      solarRadiation,
      temperature2m,
      humidity2m,
      soilMoisture,
      snowAcc,
      highResPrecip,
      highResWind10,
      highResWind80,
      highResWindPower80Data,
      highResSolar,
      highResTemp,
      highResHumidity,
    } = params;

    if (!forecast) return null;

    if (forecast.includes("processed-")) {
      return AssetGraphService.buildFlatProcessedModelData(
        processedModelData,
        forecast.replace("processed-", ""),
        fcVariation
      );
    }

    let data = null;
    let highRes = null;
    const format = interpolateChecked ? "interpolated" : "raw";
    let variationEquivalent = fcVariation;

    switch (forecast) {
      case "Precipitation":
        data = precipitation;
        highRes = highResPrecip?.[fcVariation];
        break;
      case "Wind10":
        data = wind10;
        highRes = highResWind10?.[fcVariation];
        break;
      case "Wind80":
        if (fcVariation === "Power") {
          data = windPower80Data;
          highRes = highResWindPower80Data;
          break;
        }
        data = wind80;
        highRes = highResWind80?.[fcVariation];
        break;
      case "Solar":
        if (fcVariation === "Power") {
          data = solarPowerData;
          highRes = solarPowerData?.ohhfc;
          break;
        }
        if (fcVariation === "sWave") {
          variationEquivalent = "sWave";
          highRes = highResSolar?.solarS;
        }
        if (fcVariation === "lWave") {
          variationEquivalent = "lWave";
          highRes = highResSolar?.solarL;
        }
        data = solarRadiation;
        break;
      case "Temperature":
        data = temperature2m;
        highRes = highResTemp;
        break;
      case "Humidity":
        data = humidity2m;
        highRes = highResHumidity;
        break;
      case "SoilMoisture":
        data = soilMoisture;
        break;
      case "SnowAcc":
        data = snowAcc;
        highRes = null;
        break;
      default:
        break;
    }

    if (!data) {
      return null;
    }

    const variationData = data[format]?.[variationEquivalent];
    if (!variationData) {
      return null;
    }

    const flatData = {
      ...variationData,
      ...variationData.percentiles,
      hourly: highRes,
    } as Record<string, any>;
    delete flatData.percentiles;

    // A series can still be loading (the short-range "hourly" data lands after the main forecast, most visibly when
    // switching assets), so only arrays become columns — an undefined one used to crash the table
    Object.keys(flatData).forEach((key) => {
      if (!Array.isArray(flatData[key])) delete flatData[key];
    });

    const dataSetKeys = Object.keys(flatData);
    const tabularData: Record<string, any> = {};

    dataSetKeys.forEach((key) => {
      const isoLabelledData = toIsoRecords(flatData[key]);
      isoLabelledData.forEach((record) => {
        if (!tabularData[record.x]) {
          tabularData[record.x] = {
            dateTime: record.x,
            ...createObjectWithPlaceholders(dataSetKeys, "-"),
          };
        }
        tabularData[record.x][key] = record.y.toFixed(3);
      });
    });

    return Object.values(tabularData).sort((a: any, b: any) =>
      a.dateTime.localeCompare(b.dateTime)
    );
  }

  static convertToCSV(objArray: any[], metadata: Record<string, string>) {
    if (!objArray.length) return "";

    let csvContent = "";

    Object.keys(metadata).forEach((item) => {
      let line = `${item}: ${metadata[item]}`;
      csvContent += line + "\r\n";
    });
    csvContent += "\r\n";

    const allKeys = new Set<string>();
    objArray.forEach((item) => {
      Object.keys(item).forEach((key) => allKeys.add(key));
    });
    const headers = Array.from(allKeys);
    const reorderedHeaders = [
      "dateTime",
      ...headers.filter((header) => header !== "dateTime"),
    ];
    const reNamedHeader = [
      "Datetime",
      ...headers.filter((header) => header !== "dateTime"),
    ];
    csvContent += reNamedHeader.join(",") + "\r\n";

    objArray.forEach((item) => {
      let line = reorderedHeaders.map((field) => item[field]).join(",");
      csvContent += line + "\r\n";
    });

    return csvContent;
  }

  static buildCsvMetadata(params: {
    selectedAsset: any;
    activeForecast: string;
    forecastVariation: string;
    dateTimeCycle: string | null;
    processedModelData: any;
  }) {
    const { selectedAsset, activeForecast, forecastVariation, dateTimeCycle, processedModelData } =
      params;

    let forecastIssuedDate;
    if (activeForecast.includes("processed-")) {
      forecastIssuedDate =
        processedModelData?.[activeForecast.split("-")[1]]?.forecast_issued;
    } else {
      if (!dateTimeCycle) {
        return null;
      }
      const [year, month, day, hour] = dateTimeCycle.split("-");
      forecastIssuedDate = new Date(
        Date.UTC(year, month - 1, day, hour)
      ).toISOString();
    }

    return {
      Name: selectedAsset.name,
      Description: selectedAsset.description,
      Location: `${selectedAsset.lat}, ${selectedAsset.lng}`,
      "Data Type": `${activeForecast} ${forecastVariation}`,
      "Forecast Issued": forecastIssuedDate,
      ...(activeForecast === "Wind80" &&
        forecastVariation === "Power" && {
          "Wind Energy Model": selectedAsset?.windCurve?.name,
        }),
      ...(activeForecast === "Solar" &&
        forecastVariation === "Power" && {
          "Solar Energy Model": selectedAsset?.solarCurve?.name,
        }),
    };
  }

  static buildCsvFilename(params: {
    selectedAsset: any;
    activeForecast: string;
    forecastVariation: string;
    dateTimeCycle: string | null;
    interpolateChecked: boolean;
  }) {
    const { selectedAsset, activeForecast, forecastVariation, dateTimeCycle, interpolateChecked } =
      params;
    const interpolatedSuffix = interpolateChecked ? "_interpolated" : "";
    return `${selectedAsset?.name}-${activeForecast}-${forecastVariation}-${dateTimeCycle}${interpolatedSuffix}`
      ?.trim()
      .replace(".", "")
      .replace(/ /g, "-");
  }

  // Composed-forecast CSV variant. There's no dateTimeCycle / variation
  // concept for composed forecasts (one combined chart), so the metadata
  // and filename are derived from the composed forecast itself.
  static buildComposedCsvMetadata(params: {
    selectedAsset: any;
    composedForecast: any;
  }) {
    const { selectedAsset, composedForecast } = params;
    return {
      Name: selectedAsset?.name,
      Description: selectedAsset?.description,
      Location: `${selectedAsset?.lat}, ${selectedAsset?.lng}`,
      "Data Type": `Composed Forecast: ${composedForecast?.name || "Combined"}`,
      "Forecast Issued": new Date().toISOString(),
    };
  }

  static buildComposedCsvFilename(params: {
    selectedAsset: any;
    composedForecast: any;
  }) {
    const { selectedAsset, composedForecast } = params;
    const stamp = new Date().toISOString().slice(0, 10);
    return `${selectedAsset?.name}-composed-${composedForecast?.name || composedForecast?.id}-${stamp}`
      ?.trim()
      .replace(".", "")
      .replace(/ /g, "-");
  }

  static downloadCsv(csvStr: string, filename: string) {
    const blob = new Blob([csvStr], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
